import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LoginModal from "../../components/LoginModal/LoginModal";
import Topbar from "../../components/Topbar/Topbar";
import { useGameConfig } from "../../context/GameConfig";
import "./Home.css";

import { logActivity } from "../../utils/activity";
import { loadGmHudConfig } from "../../utils/config";

function formatRoomName(id) {
    return id
        .replace(/-/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
}

export default function Home() {
    const { roomMap, error, isLoggedIn, setIsLoggedIn } = useGameConfig();
    const [showLoginModal, setShowLoginModal] = useState(!isLoggedIn);
    const [config, setConfig] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadGmHudConfig().then(cfg => { if (cfg) setConfig(cfg); });
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("accessToken");
        if (!token) { setShowLoginModal(true); return; }
        const wsUrl = process.env.REACT_APP_GAMES_CLOUD_URL || "";
        const validateUrl = wsUrl.replace(/^ws/, "http").replace(/ws$/, "auth/token/validate");
        fetch(validateUrl, { method: "POST", headers: { Authorization: `Bearer ${token}` } })
            .then(r => {
                if (r.ok) { setIsLoggedIn(true); setShowLoginModal(false); }
                else { localStorage.removeItem("accessToken"); setShowLoginModal(true); }
            })
            .catch(() => setShowLoginModal(true));
    }, []);

    const handleLogin = useCallback(() => {
        setIsLoggedIn(true);
        setShowLoginModal(false);
        logActivity("login");
    }, [setIsLoggedIn]);

    function handleLogout() {
        logActivity("logout");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userEmail");
        setIsLoggedIn(false);
        setShowLoginModal(true);
    }

    function handleRoomClick(room, locationKey) {
        logActivity("open_room", `${locationKey}/${room}`);
        navigate(`/game/${room}`, { state: { location: locationKey } });
    }

    const userEmail = localStorage.getItem("userEmail") || "";
    const userCfg = config?.users?.[userEmail] || null;
    const allowedLocations = userCfg?.locations || [];

    const locationKeys = [...new Set(roomMap.map(r => r.locationKey))];
    // Local development with a user the staff sheet does not know (e.g. the test_game JWT):
    // show every location, so the HUDs can be opened. Permissions are enforced server-side.
    const devUnknownUser = process.env.NODE_ENV === "development" && !userCfg;
    const visibleLocations = isLoggedIn && config && !devUnknownUser
        ? locationKeys.filter(k => allowedLocations.includes(k))
        : locationKeys;

    const locationDisplayName = key =>
        config?.locations?.[key]?.displayName || process.env[`REACT_APP_LOCATION_${key}`] || key;

    return (
        <>
            {isLoggedIn && <Topbar userEmail={userEmail} onLogout={handleLogout} />}

            <div className="home-content">
                {error && <div className="home-error">{error}</div>}

                {isLoggedIn && config && visibleLocations.length === 0 && (
                    <div className="home-empty">Ingen lokationer tildelt din bruger.</div>
                )}

                {visibleLocations.map(locationKey => {
                    const rooms = roomMap.filter(r => r.locationKey === locationKey);
                    return (
                        <div key={locationKey} className="location-section">
                            <div className="location-header">
                                <span className="location-name">{locationDisplayName(locationKey)}</span>
                            </div>
                            <div className="room-grid">
                                {rooms.map(({ room, title }) => (
                                    <div
                                        key={room}
                                        className="room-card"
                                        onClick={() => handleRoomClick(room, locationKey)}
                                    >
                                        <div className="room-card-name">{title || formatRoomName(room)}</div>
                                        <div className="room-card-sub">Åbn HUD</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {showLoginModal && <LoginModal onLogin={handleLogin} />}
        </>
    );
}
